import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import ConfirmModal from "../components/ConfirmModal";
import {
  Users,
  Star,
  Mail,
  Phone,
  Trash2,
  FileDown,
  Check,
  ArrowLeft,
  Calendar,
  Clock,
  Download,
  Bell,
  Send,
  ChevronLeft,
  ChevronRight,
  Image,
  Link as LinkIcon,
  Pin,
  X as XIcon,
  Upload,
  Loader2,
  Pencil,
  PinOff,
} from "lucide-react";
import {
  getProfile,
  getLeads,
  updateLead,
  deleteLead as deleteLeadApi,
  getReviews,
  subscribePush,
  unsubscribePush,
  getAppointments,
  updateAppointment,
  downloadAppointmentIcal,
  getOwnerNotifications,
  sendNotification,
  updateNotification,
  deleteNotification,
  uploadProfileImage,
} from "../services/api";
import {
  subscribeToPush,
  unsubscribeFromPush,
  getPushSubscription,
  isPushSupported,
} from "../services/pushNotifications";
import { useTranslation } from "../i18n/LanguageContext";

function WhatsAppIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );
}

// Call + WhatsApp buttons for a visitor phone number
function PhoneActions({ phone, t }) {
  const raw = phone.replace(/[^0-9]/g, "");
  // wa.me needs the country code; visitors usually type a 10-digit Mexican number
  const digits = raw.length === 10 ? `52${raw}` : raw;
  const tel = phone.replace(/[^0-9+]/g, "");
  return (
    <div className="flex flex-wrap items-center gap-2 mt-2">
      <a
        href={`https://wa.me/${digits}`}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-500 text-white text-xs font-semibold hover:bg-green-600 transition"
      >
        <WhatsAppIcon className="h-4 w-4" /> {t("crm.whatsapp")}
      </a>
      <a
        href={`tel:${tel}`}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 text-xs font-semibold hover:bg-gray-50 transition"
      >
        <Phone className="h-4 w-4" /> {t("crm.call")}
      </a>
      <span className="text-xs text-gray-500">{phone}</span>
    </div>
  );
}

export default function ProfileCrmPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const activeTab = tabParam === "reviews" ? "reviews" : tabParam === "appointments" ? "appointments" : tabParam === "push_notifications" ? "push_notifications" : "leads";
  const setActiveTab = (tab) => setSearchParams(tab === "leads" ? {} : { tab }, { replace: true });

  const [profileName, setProfileName] = useState("");

  // Leads state
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadsFilter, setLeadsFilter] = useState("");
  const [editingNotes, setEditingNotes] = useState({});
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);

  // Reviews state
  const [reviews, setReviews] = useState([]);
  const [reviewsData, setReviewsData] = useState({ total: 0, avg_rating: 0, rating_counts: {} });
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewPeriod, setReviewPeriod] = useState("all");
  const [reviewRating, setReviewRating] = useState(0);

  // Appointments state
  const [appointments, setAppointments] = useState([]);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  const [appointmentFilter, setAppointmentFilter] = useState("");

  // Push notifications state
  const [notifTitle, setNotifTitle] = useState("");
  const [notifBody, setNotifBody] = useState("");
  const [notifImage, setNotifImage] = useState("");
  const [notifImageUploading, setNotifImageUploading] = useState(false);
  const notifImageRef = useRef(null);
  const [notifUrlMode, setNotifUrlMode] = useState("profile");
  const [notifCustomUrl, setNotifCustomUrl] = useState("");
  const [notifBlockId, setNotifBlockId] = useState("");
  const [notifPinned, setNotifPinned] = useState(false);
  const [notifSending, setNotifSending] = useState(false);
  const [notifData, setNotifData] = useState({ notifications: [], subscriber_count: 0 });
  const [notifLoaded, setNotifLoaded] = useState(false);
  const [notifEditing, setNotifEditing] = useState(null); // { id, title, body }
  const [notifSaving, setNotifSaving] = useState(false);
  const [profileBlocks, setProfileBlocks] = useState([]);

  // Tab scroll indicators
  const tabsRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const updateScrollArrows = useCallback(() => {
    const el = tabsRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);
  useEffect(() => {
    const el = tabsRef.current;
    if (!el) return;
    updateScrollArrows();
    el.addEventListener("scroll", updateScrollArrows, { passive: true });
    window.addEventListener("resize", updateScrollArrows);
    return () => {
      el.removeEventListener("scroll", updateScrollArrows);
      window.removeEventListener("resize", updateScrollArrows);
    };
  }, [updateScrollArrows]);

  // Confirm modal
  const [confirmModal, setConfirmModal] = useState(null);
  const confirmRef = useRef(null);
  const showConfirm = (message, onConfirm) => {
    confirmRef.current = onConfirm;
    setConfirmModal(message);
  };

  // Toast
  const [toast, setToast] = useState(null);
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timer);
  }, [toast]);
  const showToast = (message, type = "success") => setToast({ message, type });

  const NOTIF_BLOCK_TYPES = ["gallery", "image_video", "map", "google_review", "appointments", "payment", "contact_form"];

  // Fetch profile name + blocks
  useEffect(() => {
    getProfile(slug).then((res) => {
      const p = res.data.profile || res.data;
      setProfileName(p.profile_name || p.name || slug);
      const blocks = (p.blocks || []).filter((b) => NOTIF_BLOCK_TYPES.includes(b.block_type));
      setProfileBlocks(blocks);
    }).catch(() => {});
  }, [slug]);

  // Fetch leads
  const fetchLeads = useCallback(async () => {
    setLeadsLoading(true);
    try {
      const params = leadsFilter ? { status: leadsFilter } : {};
      const res = await getLeads(slug, params);
      setLeads(res.data.leads || []);
    } catch {
      setLeads([]);
    }
    setLeadsLoading(false);
  }, [slug, leadsFilter]);

  useEffect(() => {
    getPushSubscription().then((sub) => setPushEnabled(!!sub));
  }, []);

  useEffect(() => {
    if (activeTab === "leads") fetchLeads();
  }, [activeTab, fetchLeads]);

  // Fetch reviews
  useEffect(() => {
    if (activeTab !== "reviews") return;
    setReviewsLoading(true);
    const params = { period: reviewPeriod };
    if (reviewRating) params.rating = reviewRating;
    getReviews(slug, params)
      .then((res) => {
        setReviews(res.data.reviews || []);
        setReviewsData({
          total: res.data.total || 0,
          avg_rating: res.data.avg_rating || 0,
          rating_counts: res.data.rating_counts || {},
        });
      })
      .catch(() => {})
      .finally(() => setReviewsLoading(false));
  }, [activeTab, slug, reviewPeriod, reviewRating]);

  // Fetch appointments
  const fetchAppointments = useCallback(async () => {
    setAppointmentsLoading(true);
    try {
      const params = appointmentFilter ? { status: appointmentFilter } : {};
      const res = await getAppointments(slug, params);
      setAppointments(res.data.appointments || []);
    } catch {
      setAppointments([]);
    }
    setAppointmentsLoading(false);
  }, [slug, appointmentFilter]);

  useEffect(() => {
    if (activeTab === "appointments") fetchAppointments();
  }, [activeTab, fetchAppointments]);

  // Fetch push notifications
  useEffect(() => {
    if (activeTab !== "push_notifications" || notifLoaded) return;
    getOwnerNotifications(slug).then((res) => {
      setNotifData(res.data);
      setNotifLoaded(true);
    }).catch(() => setNotifLoaded(true));
  }, [activeTab, slug, notifLoaded]);

  const compressAndUploadNotifImage = async (file) => {
    setNotifImageUploading(true);
    try {
      const blob = await new Promise((resolve) => {
        const img = new window.Image();
        img.onload = () => {
          const MAX = 1200;
          let w = img.width, h = img.height;
          if (w > MAX || h > MAX) {
            const scale = MAX / Math.max(w, h);
            w = Math.round(w * scale);
            h = Math.round(h * scale);
          }
          const canvas = document.createElement("canvas");
          canvas.width = w;
          canvas.height = h;
          canvas.getContext("2d").drawImage(img, 0, 0, w, h);
          canvas.toBlob((b) => resolve(b), "image/jpeg", 0.85);
        };
        img.src = URL.createObjectURL(file);
      });
      const formData = new FormData();
      formData.append("file", blob, "notification.jpg");
      formData.append("type", "notification");
      const res = await uploadProfileImage(slug, formData);
      setNotifImage(res.data.url);
    } catch {
      showToast("Failed to upload image", "error");
    } finally {
      setNotifImageUploading(false);
    }
  };

  const handleSendNotification = async () => {
    if (!notifTitle.trim() || notifSending) return;
    setNotifSending(true);
    let url = "";
    if (notifUrlMode === "custom") url = notifCustomUrl.trim();
    else if (notifUrlMode === "block" && notifBlockId) url = `/p/${slug}#block-${notifBlockId}`;
    try {
      const payload = { title: notifTitle.trim(), body: notifBody.trim() };
      if (notifImage.trim()) payload.image = notifImage.trim();
      if (url) payload.url = url;
      if (notifPinned) payload.pinned = true;
      const res = await sendNotification(slug, payload);
      const msg = t("editor.app.notifSent")
        .replace("{sent}", res.data.sent)
        .replace("{total}", res.data.total);
      showToast(msg);
      setNotifTitle("");
      setNotifBody("");
      setNotifImage("");
      setNotifUrlMode("profile");
      setNotifCustomUrl("");
      setNotifBlockId("");
      setNotifPinned(false);
      setNotifData((prev) => ({
        ...prev,
        notifications: [res.data.notification, ...prev.notifications],
      }));
    } catch {
      showToast("Failed to send notification", "error");
    } finally {
      setNotifSending(false);
    }
  };

  const handleUpdateAppointmentStatus = async (id, status) => {
    try {
      await updateAppointment(id, { status });
      setAppointments((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
    } catch {
      showToast(t("contactForm.error"), "error");
    }
  };

  const handleDownloadIcal = async (id) => {
    try {
      const res = await downloadAppointmentIcal(id);
      const blob = new Blob([res.data], { type: "text/calendar" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `appointment-${id}.ics`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {}
  };

  // Handlers
  const handleTogglePush = async () => {
    setPushLoading(true);
    try {
      if (pushEnabled) {
        const sub = await getPushSubscription();
        if (sub) {
          await unsubscribePush(sub.endpoint);
          await unsubscribeFromPush();
        }
        setPushEnabled(false);
      } else {
        const sub = await subscribeToPush();
        if (sub) {
          await subscribePush(sub);
          setPushEnabled(true);
        }
      }
    } catch {}
    setPushLoading(false);
  };

  const handleUpdateLeadStatus = async (leadId, status) => {
    try {
      await updateLead(slug, leadId, { status });
      setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, status } : l)));
    } catch {
      showToast(t("contactForm.error"), "error");
    }
  };

  const handleSaveLeadNotes = async (leadId) => {
    const notes = editingNotes[leadId];
    if (notes === undefined) return;
    try {
      await updateLead(slug, leadId, { notes });
      setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, notes } : l)));
      setEditingNotes((prev) => {
        const copy = { ...prev };
        delete copy[leadId];
        return copy;
      });
    } catch {
      showToast(t("contactForm.error"), "error");
    }
  };

  const replaceNotification = (updated) =>
    setNotifData((prev) => ({
      ...prev,
      notifications: prev.notifications.map((n) => (n.id === updated.id ? updated : n)),
    }));

  const handleToggleNotifPin = async (n) => {
    try {
      const res = await updateNotification(slug, n.id, { pinned: !n.pinned });
      replaceNotification(res.data.notification);
    } catch {
      showToast(t("contactForm.error"), "error");
    }
  };

  const handleSaveNotifEdit = async () => {
    if (!notifEditing?.title.trim()) return;
    setNotifSaving(true);
    try {
      const res = await updateNotification(slug, notifEditing.id, {
        title: notifEditing.title,
        body: notifEditing.body,
      });
      replaceNotification(res.data.notification);
      setNotifEditing(null);
    } catch {
      showToast(t("contactForm.error"), "error");
    } finally {
      setNotifSaving(false);
    }
  };

  const handleDeleteNotification = (id) => {
    showConfirm(t("editor.app.notifDeleteConfirm"), async () => {
      try {
        await deleteNotification(slug, id);
        setNotifData((prev) => ({
          ...prev,
          notifications: prev.notifications.filter((n) => n.id !== id),
        }));
      } catch {
        showToast(t("contactForm.error"), "error");
      }
    });
  };

  const handleDeleteLead = (leadId) => {
    showConfirm(t("crm.deleteConfirm"), async () => {
      try {
        await deleteLeadApi(slug, leadId);
        setLeads((prev) => prev.filter((l) => l.id !== leadId));
      } catch {
        showToast(t("contactForm.error"), "error");
      }
    });
  };

  const exportLeadsCsv = () => {
    const headers = ["Name", "Email", "WhatsApp", "Message", "Status", "Source", "Date"];
    const rows = leads.map((l) => [
      l.name, l.email || "", l.whatsapp || "", (l.message || "").replace(/\n/g, " "),
      l.status, l.source_ref || "direct", l.created_at ? new Date(l.created_at).toLocaleString() : "",
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads-${slug}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-5xl">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-2 rounded-lg shadow-lg text-sm font-medium text-white ${
          toast.type === "error" ? "bg-red-500" : "bg-green-500"
        }`}>
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate("/app/profiles")}
          className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-gray-900">CRM</h1>
          <p className="text-sm text-gray-500">{profileName}</p>
        </div>
        {isPushSupported() && (
          <button
            onClick={handleTogglePush}
            disabled={pushLoading}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition ${
              pushEnabled
                ? "bg-green-50 text-green-700 border border-green-200"
                : "bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100"
            }`}
            title={t("crm.pushTooltip")}
          >
            {pushLoading ? (
              <div className="animate-spin rounded-full h-3 w-3 border-2 border-current border-t-transparent" />
            ) : pushEnabled ? (
              <Check className="h-3 w-3" />
            ) : (
              <Bell className="h-3 w-3" />
            )}
            {pushEnabled ? t("crm.pushEnabled") : t("crm.pushEnable")}
          </button>
        )}
      </div>

      {/* Tab navigation */}
      <div className="relative mb-6">
        {canScrollLeft && (
          <button
            onClick={() => tabsRef.current?.scrollBy({ left: -120, behavior: "smooth" })}
            className="absolute left-0 top-0 bottom-0 z-10 flex items-center pl-1 pr-2 bg-gradient-to-r from-white via-white/90 to-transparent"
          >
            <ChevronLeft className="w-4 h-4 text-gray-400" />
          </button>
        )}
        <div
          ref={tabsRef}
          className="flex gap-1 border-b border-gray-200 overflow-x-auto"
          style={{ scrollbarWidth: "none" }}
        >
          <button
            onClick={() => setActiveTab("leads")}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition ${
              activeTab === "leads"
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            <Users className="w-4 h-4" />
            {t("crm.title")}
          </button>
          <button
            onClick={() => setActiveTab("reviews")}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition ${
              activeTab === "reviews"
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            <Star className="w-4 h-4" />
            {t("editor.tab.reviews")}
          </button>
          <button
            onClick={() => setActiveTab("appointments")}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition ${
              activeTab === "appointments"
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            <Calendar className="w-4 h-4" />
            {t("crm.appointments")}
          </button>
          <button
            onClick={() => setActiveTab("push_notifications")}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition ${
              activeTab === "push_notifications"
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            <Bell className="w-4 h-4" />
            {t("crm.pushNotifications")}
          </button>
        </div>
        {canScrollRight && (
          <button
            onClick={() => tabsRef.current?.scrollBy({ left: 120, behavior: "smooth" })}
            className="absolute right-0 top-0 bottom-0 z-10 flex items-center pr-1 pl-2 bg-gradient-to-l from-white via-white/90 to-transparent"
          >
            <ChevronRight className="w-4 h-4 text-gray-400" />
          </button>
        )}
      </div>

      {/* ==================== LEADS TAB ==================== */}
      {activeTab === "leads" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{t("crm.title")}</h2>
              <p className="text-sm text-gray-500">{t("crm.subtitle")}</p>
            </div>
            <div className="flex items-center gap-2">
              {leads.length > 0 && (
                <button
                  onClick={exportLeadsCsv}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 transition"
                >
                  <FileDown className="h-3 w-3" />
                  {t("crm.exportCsv")}
                </button>
              )}
            </div>
          </div>

          <div className="flex gap-2 flex-wrap">
            {[
              { value: "", tKey: "crm.allStatuses" },
              { value: "new", tKey: "crm.statusNew" },
              { value: "contacted", tKey: "crm.statusContacted" },
              { value: "converted", tKey: "crm.statusConverted" },
              { value: "archived", tKey: "crm.statusArchived" },
            ].map((s) => (
              <button
                key={s.value}
                onClick={() => setLeadsFilter(s.value)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                  leadsFilter === s.value
                    ? "bg-indigo-600 text-white"
                    : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                }`}
              >
                {t(s.tKey)}
                {s.value === "" && leads.length > 0 && (
                  <span className="ml-1 text-xs opacity-70">({leads.length})</span>
                )}
              </button>
            ))}
          </div>

          {leadsLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-indigo-500 border-t-transparent" />
            </div>
          ) : leads.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
              <Users className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">{t("crm.noLeads")}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {leads.map((lead) => {
                const statusColors = {
                  new: "bg-blue-50 text-blue-700 border-blue-200",
                  contacted: "bg-yellow-50 text-yellow-700 border-yellow-200",
                  converted: "bg-green-50 text-green-700 border-green-200",
                  archived: "bg-gray-50 text-gray-500 border-gray-200",
                };
                return (
                  <div key={lead.id} className="bg-white rounded-xl border border-gray-200 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-sm font-semibold text-gray-900 truncate">{lead.name}</h3>
                          <select
                            value={lead.status}
                            onChange={(e) => handleUpdateLeadStatus(lead.id, e.target.value)}
                            className={`text-xs font-medium px-2 py-0.5 rounded-full border cursor-pointer ${statusColors[lead.status] || statusColors.new}`}
                          >
                            <option value="new">{t("crm.statusNew")}</option>
                            <option value="contacted">{t("crm.statusContacted")}</option>
                            <option value="converted">{t("crm.statusConverted")}</option>
                            <option value="archived">{t("crm.statusArchived")}</option>
                          </select>
                        </div>
                        <div className="flex flex-wrap gap-3 text-xs text-gray-500 mb-1">
                          {lead.email && (
                            <a href={`mailto:${lead.email}`} className="flex items-center gap-1 hover:text-indigo-600 transition">
                              <Mail className="h-3 w-3" /> {lead.email}
                            </a>
                          )}
                          {lead.source_ref && (
                            <span className="flex items-center gap-1 text-gray-400">
                              {t("crm.source")}: {lead.source_ref}
                            </span>
                          )}
                          <span className="text-gray-400">
                            {new Date(lead.created_at).toLocaleString(undefined, {
                              month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                            })}
                          </span>
                        </div>
                        {lead.whatsapp && <PhoneActions phone={lead.whatsapp} t={t} />}
                        {lead.message && (
                          <p className="text-sm text-gray-600 mt-2 whitespace-pre-wrap">{lead.message}</p>
                        )}
                      </div>
                      <button
                        onClick={() => handleDeleteLead(lead.id)}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition flex-shrink-0"
                        title={t("crm.delete")}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <textarea
                        value={editingNotes[lead.id] !== undefined ? editingNotes[lead.id] : (lead.notes || "")}
                        onChange={(e) => setEditingNotes((prev) => ({ ...prev, [lead.id]: e.target.value }))}
                        onBlur={() => handleSaveLeadNotes(lead.id)}
                        placeholder={t("crm.notesPlaceholder")}
                        rows={1}
                        className="w-full px-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 outline-none resize-none"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ==================== REVIEWS TAB ==================== */}
      {activeTab === "reviews" && (
        <div className="space-y-6">
          {reviewsLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-3">
                <select
                  value={reviewPeriod}
                  onChange={(e) => setReviewPeriod(e.target.value)}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="all">{t("reviews.periodAll")}</option>
                  <option value="today">{t("reviews.periodToday")}</option>
                  <option value="week">{t("reviews.periodWeek")}</option>
                  <option value="month">{t("reviews.periodMonth")}</option>
                  <option value="3months">{t("reviews.period3Months")}</option>
                  <option value="6months">{t("reviews.period6Months")}</option>
                  <option value="year">{t("reviews.periodYear")}</option>
                </select>
                <div className="flex gap-1">
                  <button
                    onClick={() => setReviewRating(0)}
                    className={`px-3 py-2 text-sm rounded-lg border transition ${
                      reviewRating === 0
                        ? "border-indigo-600 bg-indigo-50 text-indigo-700 font-medium"
                        : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    {t("reviews.allStars")}
                  </button>
                  {[5, 4, 3, 2, 1].map((s) => (
                    <button
                      key={s}
                      onClick={() => setReviewRating(reviewRating === s ? 0 : s)}
                      className={`px-2.5 py-2 text-sm rounded-lg border transition flex items-center gap-1 ${
                        reviewRating === s
                          ? "border-indigo-600 bg-indigo-50 text-indigo-700 font-medium"
                          : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      {s}
                      <Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-gray-900">{reviewsData.total}</div>
                  <div className="text-xs text-gray-500 mt-1">{t("reviews.totalReviews")}</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-yellow-500 flex items-center justify-center gap-1">
                    {reviewsData.avg_rating > 0 ? reviewsData.avg_rating : "—"}
                    <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{t("reviews.avgRating")}</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {(parseInt(reviewsData.rating_counts["4"] || 0) + parseInt(reviewsData.rating_counts["5"] || 0))}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{t("reviews.positive")}</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-red-500">
                    {(parseInt(reviewsData.rating_counts["1"] || 0) + parseInt(reviewsData.rating_counts["2"] || 0) + parseInt(reviewsData.rating_counts["3"] || 0))}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{t("reviews.needsWork")}</div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">{t("reviews.breakdown")}</h3>
                <div className="space-y-2">
                  {[5, 4, 3, 2, 1].map((star) => {
                    const count = parseInt(reviewsData.rating_counts[String(star)] || 0);
                    const pct = reviewsData.total > 0 ? (count / reviewsData.total) * 100 : 0;
                    return (
                      <div key={star} className="flex items-center gap-3">
                        <div className="flex items-center gap-0.5 w-16 justify-end">
                          <span className="text-sm text-gray-600">{star}</span>
                          <Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
                        </div>
                        <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full bg-yellow-400 transition-all"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500 w-8 text-right">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {reviews.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                  <Star className="w-10 h-10 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500 text-sm">{t("reviews.noReviews")}</p>
                  <p className="text-gray-400 text-xs mt-1">{t("reviews.noReviewsDesc")}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {reviews.map((review) => (
                    <div key={review.id} className="bg-white border border-gray-200 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex gap-0.5">
                          {[1, 2, 3, 4, 5].map((i) => (
                            <Star
                              key={i}
                              className={`w-4 h-4 ${i <= review.rating ? "text-yellow-400 fill-yellow-400" : "text-gray-200"}`}
                            />
                          ))}
                        </div>
                        <span className="text-xs text-gray-400">
                          {new Date(review.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      {review.feedback && (
                        <p className="text-sm text-gray-700 mt-1">{review.feedback}</p>
                      )}
                      {!review.feedback && review.rating >= 4 && (
                        <p className="text-xs text-gray-400 italic">{t("reviews.redirectedToGoogle")}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ==================== APPOINTMENTS TAB ==================== */}
      {activeTab === "appointments" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{t("crm.appointments")}</h2>
              <p className="text-sm text-gray-500">{t("crm.appointmentsSubtitle")}</p>
            </div>
          </div>

          <div className="flex gap-2 flex-wrap">
            {[
              { value: "", tKey: "crm.allStatuses" },
              { value: "pending", tKey: "crm.apptPending" },
              { value: "confirmed", tKey: "crm.apptConfirmed" },
              { value: "cancelled", tKey: "crm.apptCancelled" },
              { value: "completed", tKey: "crm.apptCompleted" },
            ].map((s) => (
              <button
                key={s.value}
                onClick={() => setAppointmentFilter(s.value)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                  appointmentFilter === s.value
                    ? "bg-indigo-600 text-white"
                    : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                }`}
              >
                {t(s.tKey)}
              </button>
            ))}
          </div>

          {appointmentsLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-indigo-500 border-t-transparent" />
            </div>
          ) : appointments.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
              <Calendar className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">{t("crm.noAppointments")}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {appointments.map((appt) => {
                const statusColors = {
                  pending: "bg-yellow-50 text-yellow-700 border-yellow-200",
                  confirmed: "bg-green-50 text-green-700 border-green-200",
                  cancelled: "bg-red-50 text-red-600 border-red-200",
                  completed: "bg-blue-50 text-blue-700 border-blue-200",
                };
                return (
                  <div key={appt.id} className="bg-white rounded-xl border border-gray-200 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-sm font-semibold text-gray-900">{appt.service_name}</h3>
                          <select
                            value={appt.status}
                            onChange={(e) => handleUpdateAppointmentStatus(appt.id, e.target.value)}
                            className={`text-xs font-medium px-2 py-0.5 rounded-full border cursor-pointer ${statusColors[appt.status] || ""}`}
                          >
                            <option value="pending">{t("crm.apptPending")}</option>
                            <option value="confirmed">{t("crm.apptConfirmed")}</option>
                            <option value="completed">{t("crm.apptCompleted")}</option>
                            <option value="cancelled">{t("crm.apptCancelled")}</option>
                          </select>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-500 mb-1">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {appt.date && new Date(appt.date + "T00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {appt.start_time} - {appt.end_time}
                          </span>
                          {appt.service_price > 0 && (
                            <span className="font-medium text-green-600">${appt.service_price.toFixed(2)}</span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                          <span className="font-medium text-gray-700">{appt.visitor_name}</span>
                          {appt.visitor_email && (
                            <a href={`mailto:${appt.visitor_email}`} className="flex items-center gap-1 hover:text-indigo-600 transition">
                              <Mail className="h-3 w-3" /> {appt.visitor_email}
                            </a>
                          )}
                        </div>
                        {appt.visitor_whatsapp && <PhoneActions phone={appt.visitor_whatsapp} t={t} />}
                        {appt.visitor_notes && (
                          <p className="text-xs text-gray-500 mt-2 italic">"{appt.visitor_notes}"</p>
                        )}
                      </div>
                      <button
                        onClick={() => handleDownloadIcal(appt.id)}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition flex-shrink-0"
                        title={t("crm.downloadIcal")}
                      >
                        <Download className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ==================== PUSH NOTIFICATIONS TAB ==================== */}
      {activeTab === "push_notifications" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{t("editor.app.notifications")}</h2>
              <p className="text-sm text-gray-500">{t("editor.app.notificationsDesc")}</p>
            </div>
            {notifData.subscriber_count > 0 && (
              <span className="text-sm text-gray-500">
                {notifData.subscriber_count} {t("editor.app.subscribers")}
              </span>
            )}
          </div>

          {!notifLoaded ? (
            <div className="flex justify-center py-12">
              <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : notifData.subscriber_count === 0 ? (
            <p className="text-sm text-gray-400 text-center py-12">{t("editor.app.noSubscribers")}</p>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
              <input
                type="text"
                value={notifTitle}
                onChange={(e) => setNotifTitle(e.target.value)}
                placeholder={t("editor.app.notifTitle")}
                maxLength={200}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <textarea
                value={notifBody}
                onChange={(e) => setNotifBody(e.target.value)}
                placeholder={t("editor.app.notifBody")}
                rows={2}
                maxLength={500}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              />

              {/* Image upload */}
              <div>
                <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 mb-1">
                  <Image className="w-3.5 h-3.5" />
                  {t("editor.app.notifImage")}
                </label>
                <input
                  ref={notifImageRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) compressAndUploadNotifImage(file);
                    e.target.value = "";
                  }}
                  className="hidden"
                />
                {notifImage ? (
                  <div className="relative inline-block">
                    <img src={notifImage} alt="" className="max-h-32 rounded-lg border border-gray-200 object-cover" />
                    <button
                      onClick={() => setNotifImage("")}
                      className="absolute -top-1.5 -right-1.5 p-0.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition"
                    >
                      <XIcon className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => notifImageRef.current?.click()}
                    disabled={notifImageUploading}
                    className="flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-blue-400 hover:text-blue-600 transition w-full justify-center"
                  >
                    {notifImageUploading ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> {t("editor.app.uploading")}</>
                    ) : (
                      <><Upload className="w-4 h-4" /> {t("editor.app.notifUploadImage")}</>
                    )}
                  </button>
                )}
              </div>

              {/* URL destination */}
              <div>
                <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 mb-1">
                  <LinkIcon className="w-3.5 h-3.5" />
                  {t("editor.app.notifUrl")}
                </label>
                <div className="flex gap-2 mb-2">
                  {[
                    { value: "profile", label: t("editor.app.notifUrlProfile") },
                    { value: "block", label: t("editor.app.notifUrlBlock") },
                    { value: "custom", label: t("editor.app.notifUrlCustom") },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setNotifUrlMode(opt.value)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition ${
                        notifUrlMode === opt.value
                          ? "bg-blue-50 border-blue-300 text-blue-700"
                          : "bg-white border-gray-200 text-gray-500 hover:bg-gray-50"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {notifUrlMode === "block" && (
                  <select
                    value={notifBlockId}
                    onChange={(e) => setNotifBlockId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">{t("editor.app.notifSelectBlock")}</option>
                    {profileBlocks.map((b) => {
                      const title = b.config?.title_i18n?.[language] || b.config?.title_i18n?.en || b.config?.title;
                      return (
                        <option key={b.id} value={b.id}>
                          {title || t(`editor.blockType.${b.block_type}`)}
                        </option>
                      );
                    })}
                  </select>
                )}
                {notifUrlMode === "custom" && (
                  <input
                    type="url"
                    value={notifCustomUrl}
                    onChange={(e) => setNotifCustomUrl(e.target.value)}
                    placeholder="https://..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                )}
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={notifPinned}
                    onChange={(e) => setNotifPinned(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <Pin className="w-3.5 h-3.5 text-gray-500" />
                  <span className="text-xs text-gray-600">{t("editor.app.notifPinned")}</span>
                </label>
                <button
                  onClick={handleSendNotification}
                  disabled={!notifTitle.trim() || notifSending}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
                >
                  <Send className="w-3.5 h-3.5" />
                  {notifSending ? t("editor.app.sending") : t("editor.app.sendNotification")}
                </button>
              </div>
            </div>
          )}

          {notifData.notifications.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-600 mb-3">{t("editor.app.notifHistory")}</h3>
              <div className="space-y-2">
                {notifData.notifications.map((n) => (
                  notifEditing?.id === n.id ? (
                    <div key={n.id} className="px-4 py-3 bg-white rounded-xl border border-blue-300 space-y-2">
                      <input
                        type="text"
                        value={notifEditing.title}
                        onChange={(e) => setNotifEditing((prev) => ({ ...prev, title: e.target.value }))}
                        placeholder={t("editor.app.notifTitle")}
                        maxLength={200}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <textarea
                        value={notifEditing.body}
                        onChange={(e) => setNotifEditing((prev) => ({ ...prev, body: e.target.value }))}
                        placeholder={t("editor.app.notifBody")}
                        rows={2}
                        maxLength={500}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                      />
                      <p className="text-[11px] text-gray-400">{t("editor.app.notifEditHint")}</p>
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => setNotifEditing(null)}
                          className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition"
                        >
                          {t("crm.cancel")}
                        </button>
                        <button
                          onClick={handleSaveNotifEdit}
                          disabled={!notifEditing.title.trim() || notifSaving}
                          className="px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition"
                        >
                          {notifSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : t("editor.app.notifSave")}
                        </button>
                      </div>
                    </div>
                  ) : (
                  <div key={n.id} className="flex items-start justify-between gap-3 px-4 py-3 bg-white rounded-xl border border-gray-200">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        {n.pinned && <Pin className="w-3 h-3 text-blue-500 flex-shrink-0" />}
                        <p className="text-sm font-medium text-gray-900 truncate">{n.title}</p>
                      </div>
                      {n.body && <p className="text-xs text-gray-500 truncate">{n.body}</p>}
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(n.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                        {" · "}{n.sent_count} sent
                      </p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => handleToggleNotifPin(n)}
                        className={`p-1.5 rounded-lg transition ${n.pinned ? "text-blue-600 bg-blue-50 hover:bg-blue-100" : "text-gray-400 hover:text-blue-600 hover:bg-blue-50"}`}
                        title={n.pinned ? t("editor.app.notifUnpin") : t("editor.app.notifPin")}
                      >
                        {n.pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
                      </button>
                      <button
                        onClick={() => setNotifEditing({ id: n.id, title: n.title, body: n.body || "" })}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition"
                        title={t("editor.app.notifEdit")}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteNotification(n.id)}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition"
                        title={t("crm.deleteConfirmBtn")}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  )
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <ConfirmModal
        message={confirmModal}
        confirmLabel={t("crm.deleteConfirmBtn") || "Delete"}
        cancelLabel={t("crm.cancel") || "Cancel"}
        onConfirm={() => { setConfirmModal(null); confirmRef.current?.(); }}
        onCancel={() => setConfirmModal(null)}
      />
    </div>
  );
}
